/** True when Firebase Storage rejected the write (CRM #69 / #72 / #74 / #79 / #80 staff uploads). */
export function isStorageUnauthorizedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const code = 'code' in err ? String((err as { code?: unknown }).code || '') : '';
  if (code === 'storage/unauthorized') return true;
  // Some SDK paths surface a 403 as storage/unknown with a permission message.
  if (code === 'storage/unknown') {
    const message = err instanceof Error ? err.message : String((err as { message?: unknown }).message || '');
    return /permission|unauthorized|does not have permission/i.test(message);
  }
  return false;
}

function firebaseErrorCode(err: unknown): string {
  if (!err || typeof err !== 'object' || !('code' in err)) return '';
  return String((err as { code?: unknown }).code || '').trim();
}

function firebaseErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = String((err as { message?: unknown }).message || '').trim();
    if (msg) return msg;
  }
  return '';
}

/** Surface Storage denials clearly (CRM #69 / #72 / #74 / #79 / #80 staff receipt uploads). */
export function purchaseAttachmentUploadErrorMessage(err: unknown, fileName?: string): string {
  const code = firebaseErrorCode(err);
  const label = fileName?.trim() ? `"${fileName.trim()}"` : 'This file';
  if (code === 'storage/unauthorized' || isStorageUnauthorizedError(err)) {
    return (
      `${label} could not be uploaded — storage access was denied. ` +
      'Sign out and back in so your staff role refreshes, then try again. ' +
      'If it still fails, ask an admin to deploy Storage rules and ' +
      'uploadPurchaseRequisitionAttachment / syncCrmStorageAccessClaim.'
    );
  }
  if (code === 'storage/canceled') {
    return `${label} upload was canceled.`;
  }
  if (code === 'storage/retry-limit-exceeded' || code === 'storage/unknown') {
    return `${label} upload failed. Check your connection and try again.`;
  }
  const message = firebaseErrorMessage(err);
  if (message) return message;
  return `${label} upload failed.`;
}

/**
 * Prefer the callable's HttpsError text (and include the functions/* code) so the
 * next CRM feedback report is diagnosable instead of a bare "internal".
 */
export function purchaseCallableErrorMessage(err: unknown, fileName?: string): string {
  const code = firebaseErrorCode(err);
  const label = fileName?.trim() ? `"${fileName.trim()}"` : 'This file';
  const message = firebaseErrorMessage(err);
  const cleaned = message
    .replace(/^Firebase:\s*/i, '')
    .replace(/\s*\(functions\/[a-z0-9-]+\)\.?\s*$/i, '')
    .trim();

  if (code === 'functions/not-found' || code === 'functions/unavailable') {
    return (
      `${label} could not be uploaded via the server helper (${code}). ` +
      'Ask an admin to deploy uploadPurchaseRequisitionAttachment, then try again.'
    );
  }
  if (code === 'functions/permission-denied') {
    return (
      cleaned ||
      `${label} upload was denied — you may not be allowed to attach files on this PO.`
    ) + ` (${code})`;
  }
  if (code.startsWith('functions/')) {
    return `${cleaned || `${label} upload failed`} (${code})`;
  }
  if (cleaned) return cleaned;
  return purchaseAttachmentUploadErrorMessage(err, fileName);
}

/**
 * Translate Mark purchased Firestore / client failures into a readable reason
 * (CRM #80 — prior reports only captured a generic red banner).
 */
export function purchaseMarkPurchasedErrorMessage(err: unknown): string {
  const code = firebaseErrorCode(err);
  const message = firebaseErrorMessage(err);

  if (/Unsupported field value: undefined/i.test(message)) {
    return (
      'Could not save Mark purchased — an attachment field was empty (undefined) ' +
      'and Firestore rejected the write. Try again; if it keeps failing, remove and ' +
      're-attach the receipt, then Confirm purchased.'
    );
  }
  if (code === 'permission-denied' || /Missing or insufficient permissions/i.test(message)) {
    return (
      'Could not mark this PO purchased — Firestore denied the update ' +
      `(${code || 'permission-denied'}). You may not be the requester, an approver, ` +
      'or purchasing office on this PO.'
    );
  }
  if (code.startsWith('functions/') || code.startsWith('storage/')) {
    return purchaseCallableErrorMessage(err);
  }
  if (message) {
    return code && !message.includes(code) ? `${message} (${code})` : message;
  }
  return 'Could not mark this PO purchased.';
}
